import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { fileUrl } from "../lib/apiExtra";
import { toast } from "sonner";
import { Megaphone, Sparkles, Loader2, Copy, Download, Linkedin, Facebook, Instagram, CheckCircle2, RefreshCw, CalendarDays, LayoutGrid, History, Search, X, CopyPlus, Pencil, Save } from "lucide-react";

const TONES = ["Professional", "Friendly", "Bold", "Educational", "Urgent"];
const STATES = ["Florida", "Maryland", "Massachusetts", "New Jersey", "West Virginia", "Texas", "California", "New York", "Georgia", "Pennsylvania"];

const PLATFORM_META = {
  linkedin: { label: "LinkedIn", icon: Linkedin, ring: "border-[#0A66C2]/40", chip: "bg-[#0A66C2]/10 text-[#0A66C2]" },
  facebook: { label: "Facebook", icon: Facebook, ring: "border-[#1877F2]/40", chip: "bg-[#1877F2]/10 text-[#1877F2]" },
  instagram: { label: "Instagram", icon: Instagram, ring: "border-fuchsia-400/40", chip: "bg-fuchsia-100 text-fuchsia-700" },
};
const BRAND_ORDER = ["ydeal", "titlepriority"];

function PostCard({ post, bundleId, onRegenerated }) {
  const meta = PLATFORM_META[post.platform] || { label: post.platform_label, icon: Megaphone, ring: "border-slate-200", chip: "bg-slate-100 text-slate-600" };
  const Icon = meta.icon;
  const [copied, setCopied] = useState(false);
  const [regen, setRegen] = useState(null); // "copy" | "image" | null
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ headline: "", body: "", hashtags: "" });
  const [saving, setSaving] = useState(false);
  const fullText = `${post.body}\n\n${(post.hashtags || []).map((h) => `#${h}`).join(" ")}`.trim();
  const copy = async () => {
    try { await navigator.clipboard.writeText(fullText); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success("Copied caption"); }
    catch { toast.error("Copy failed"); }
  };
  const download = () => {
    if (!post.image_url) return;
    const a = document.createElement("a");
    a.href = fileUrl(post.image_url); a.download = `${post.brand}-${post.platform}.png`; a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const regenerate = async (what) => {
    setRegen(what);
    try {
      const { data } = await api.post(`/marketing/bundle/${bundleId}/post/${post.id}/regenerate`, { what });
      onRegenerated(data);
      toast.success(what === "image" ? "New graphic ready" : "New copy ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not regenerate"); }
    finally { setRegen(null); }
  };
  const selectVariant = async (i) => {
    if (i === (post.selected_variant || 0)) return;
    try { const { data } = await api.post(`/marketing/bundle/${bundleId}/post/${post.id}/select-variant`, { index: i }); onRegenerated(data); }
    catch { toast.error("Could not switch variant"); }
  };
  const variants = post.variants || [];
  const startEdit = () => {
    setDraft({ headline: post.headline || "", body: post.body || "", hashtags: (post.hashtags || []).join(" ") });
    setEditing(true);
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      const hashtags = draft.hashtags.split(/[\s,]+/).map((h) => h.replace(/^#/, "").trim()).filter(Boolean);
      const { data } = await api.patch(`/marketing/bundle/${bundleId}/post/${post.id}`, { headline: draft.headline, body: draft.body, hashtags });
      onRegenerated(data);
      setEditing(false);
      toast.success("Caption updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid={`post-${post.brand}-${post.platform}`} className={`rounded-xl border ${meta.ring} bg-white overflow-hidden shadow-sm flex flex-col`}>
      <div className="relative aspect-square bg-slate-100">
        {post.image_url ? (
          <img src={fileUrl(post.image_url)} alt={`${post.platform_label} graphic`} className="h-full w-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.querySelector("[data-img-fallback]").style.display = "flex"; }} />
        ) : null}
        <div data-img-fallback style={{ display: post.image_url ? "none" : "flex" }} className="absolute inset-0 items-center justify-center text-center text-xs text-slate-400 px-4">
          Graphic unavailable — try regenerating
        </div>
        <span className={`absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${meta.chip}`}>
          <Icon className="h-3 w-3" /> {meta.label}
        </span>
        <button data-testid={`regen-image-${post.brand}-${post.platform}`} onClick={() => regenerate("image")} disabled={!!regen}
          title="Regenerate this graphic"
          className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-[10px] font-semibold text-white backdrop-blur hover:bg-black/70 transition-colors disabled:opacity-60">
          {regen === "image" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Image
        </button>
      </div>
      <div className="flex flex-1 flex-col p-3">
        {variants.length > 1 && (
          <div data-testid={`variant-toggle-${post.brand}-${post.platform}`} className="mb-2 flex items-center gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mr-1">Hook</span>
            {variants.map((_, i) => (
              <button key={i} data-testid={`variant-${post.brand}-${post.platform}-${i}`} onClick={() => selectVariant(i)}
                className={`h-6 w-6 rounded-md text-[11px] font-bold transition-colors ${(post.selected_variant || 0) === i ? "bg-[#1A395E] text-white" : "border border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                {String.fromCharCode(65 + i)}
              </button>
            ))}
          </div>
        )}
        {post.headline && !editing && <div className="text-sm font-bold text-[#1A395E] mb-1">{post.headline}</div>}
        {editing ? (
          <div data-testid={`edit-${post.brand}-${post.platform}`} className="space-y-2">
            <input data-testid={`edit-headline-${post.brand}-${post.platform}`} value={draft.headline}
              onChange={(e) => setDraft((d) => ({ ...d, headline: e.target.value }))} placeholder="Headline"
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm font-bold text-[#1A395E] focus:border-teal focus:outline-none" />
            <textarea data-testid={`edit-body-${post.brand}-${post.platform}`} value={draft.body} rows={5}
              onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} placeholder="Caption body"
              className="w-full rounded border border-slate-300 px-2 py-1 text-[13px] leading-snug text-slate-700 focus:border-teal focus:outline-none" />
            <input data-testid={`edit-hashtags-${post.brand}-${post.platform}`} value={draft.hashtags}
              onChange={(e) => setDraft((d) => ({ ...d, hashtags: e.target.value }))} placeholder="hashtags separated by spaces"
              className="w-full rounded border border-slate-300 px-2 py-1 text-[11px] text-teal focus:border-teal focus:outline-none" />
            <div className="flex items-center gap-2">
              <button data-testid={`edit-save-${post.brand}-${post.platform}`} onClick={saveEdit} disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#1A395E] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#24497A] transition-colors disabled:opacity-60">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
              <button data-testid={`edit-cancel-${post.brand}-${post.platform}`} onClick={() => setEditing(false)} disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-[13px] leading-snug text-slate-700 whitespace-pre-line line-clamp-[10]">{post.body}</p>
            {(post.hashtags || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {post.hashtags.map((h, i) => <span key={i} className="rounded bg-teal/10 px-1.5 py-0.5 text-[10px] font-semibold text-teal">#{h}</span>)}
              </div>
            )}
          </>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button data-testid={`edit-${post.brand}-${post.platform}-btn`} onClick={startEdit} disabled={editing}
            title="Edit this caption"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-40">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
          <button data-testid={`copy-${post.brand}-${post.platform}`} onClick={copy}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            {copied ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
          </button>
          <button data-testid={`download-${post.brand}-${post.platform}`} onClick={download} disabled={!post.image_url}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-40">
            <Download className="h-3.5 w-3.5" /> Image
          </button>
          <button data-testid={`regen-copy-${post.brand}-${post.platform}`} onClick={() => regenerate("copy")} disabled={!!regen}
            title="Rewrite this caption"
            className="inline-flex items-center gap-1.5 rounded-md border border-teal/50 px-2.5 py-1 text-[11px] font-semibold text-teal hover:bg-teal/5 transition-colors disabled:opacity-60">
            {regen === "copy" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} New copy
          </button>
          <button data-testid={`redraw-image-${post.brand}-${post.platform}`} onClick={() => regenerate("image")} disabled={!!regen}
            title="Redraw only the graphic — keeps your caption"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#1A395E]/40 px-2.5 py-1 text-[11px] font-semibold text-[#1A395E] hover:bg-[#1A395E]/5 transition-colors disabled:opacity-60">
            {regen === "image" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Redraw image
          </button>
        </div>
      </div>
    </div>
  );
}

function CalendarView({ bundle, onSchedulePost }) {
  const posts = bundle.posts || [];
  const start = bundle.schedule_start ? new Date(bundle.schedule_start + "T00:00:00") : new Date();
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const forDay = (d) => posts.filter((p) => p.scheduled_for === iso(d));
  const unscheduled = posts.filter((p) => !p.scheduled_for);
  const Chip = ({ p, showDate }) => {
    const meta = PLATFORM_META[p.platform] || { icon: Megaphone, chip: "bg-slate-100 text-slate-600" };
    const Icon = meta.icon;
    return (
      <div data-testid={`cal-post-${p.brand}-${p.platform}`} className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
        <div className="flex items-center gap-2">
          {p.image_url ? <img src={fileUrl(p.image_url)} alt="" className="h-9 w-9 rounded object-cover" /> : <div className="h-9 w-9 rounded bg-slate-100" />}
          <div className="min-w-0">
            <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${meta.chip}`}><Icon className="h-2.5 w-2.5" /> {p.platform_label}</span>
            <div className="truncate text-[11px] font-semibold text-slate-700">{p.brand === "ydeal" ? "YDeal" : "Title Priority"}</div>
          </div>
        </div>
        <div className="mt-1 line-clamp-2 text-[11px] leading-snug text-slate-600">{p.headline || p.body}</div>
        {showDate && (
          <input type="date" data-testid={`cal-date-${p.brand}-${p.platform}`} value={p.scheduled_for || ""}
            onChange={(e) => onSchedulePost(p.id, e.target.value)}
            className="mt-1.5 w-full rounded border border-slate-300 px-1.5 py-1 text-[10px] text-slate-600 focus:border-teal focus:outline-none" />
        )}
      </div>
    );
  };
  return (
    <div data-testid="calendar-view">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {days.map((d, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/70 p-2 min-h-[130px]">
            <div className="mb-2 flex items-baseline justify-between px-1">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#1A395E]">{dow[d.getDay()]}</span>
              <span className="text-[10px] text-slate-400">{d.getMonth() + 1}/{d.getDate()}</span>
            </div>
            <div className="space-y-2">
              {forDay(d).map((p) => <Chip key={p.id} p={p} />)}
              {forDay(d).length === 0 && <div className="px-1 text-[10px] text-slate-300">—</div>}
            </div>
          </div>
        ))}
      </div>
      {unscheduled.length > 0 && (
        <div className="mt-5" data-testid="calendar-unscheduled">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Unscheduled — pick a day</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {unscheduled.map((p) => <Chip key={p.id} p={p} showDate />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MarketingHub() {
  const [bundle, setBundle] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("Professional");
  const [brandSel, setBrandSel] = useState("both");
  const [statesSel, setStatesSel] = useState([]);
  const [view, setView] = useState("grid");
  const [scheduling, setScheduling] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [histQ, setHistQ] = useState("");
  const poll = useRef(null);

  const loadLatest = async () => {
    try { const { data } = await api.get("/marketing/bundles"); if (data?.length) { setBundle(data[0]); if (data[0].tone) setTone(data[0].tone); if (data[0].topic) setTopic(data[0].topic); } } catch { /* noop */ }
  };
  useEffect(() => { loadLatest(); return () => clearTimeout(poll.current); }, []);

  const pollBundle = async (id) => {
    try {
      const { data } = await api.get(`/marketing/bundle/${id}`);
      setBundle(data);
      if (data.status === "processing") { poll.current = setTimeout(() => pollBundle(id), 4000); }
      else { setGenerating(false); toast.success(`Daily bundle ready · ${data.images} graphic(s)`); }
    } catch { setGenerating(false); toast.error("Could not load bundle"); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const brands = brandSel === "both" ? ["ydeal", "titlepriority"] : [brandSel];
      const { data } = await api.post("/marketing/generate-bundle", { topic: topic.trim(), tone, brands, states: statesSel });
      setBundle(data);
      toast.info("Generating your daily bundle — writing posts and designing branded graphics…");
      clearTimeout(poll.current); poll.current = setTimeout(() => pollBundle(data.id), 3000);
    } catch (e) { setGenerating(false); toast.error(e?.response?.data?.detail || "Could not start generation"); }
  };

  const onRegenerated = (updated) => {
    setBundle((b) => b ? { ...b, posts: (b.posts || []).map((p) => p.id === updated.id ? updated : p) } : b);
  };

  const scheduleWeek = async () => {
    if (!bundle?.id) return;
    setScheduling(true);
    try {
      const today = new Date();
      const start = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const { data } = await api.post(`/marketing/bundle/${bundle.id}/schedule`, { start_date: start });
      setBundle(data); setView("calendar");
      toast.success("Bundle scheduled across the week");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule"); }
    finally { setScheduling(false); }
  };
  const schedulePost = async (postId, date) => {
    try {
      const { data } = await api.patch(`/marketing/bundle/${bundle.id}/post/${postId}/schedule`, { date });
      onRegenerated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set date"); }
  };

  const loadHistory = async (q = "") => {
    try { const { data } = await api.get(`/marketing/history${q ? `?q=${encodeURIComponent(q)}` : ""}`); setHistory(data || []); }
    catch { toast.error("Could not load history"); }
  };
  const toggleHistory = () => { const nx = !historyOpen; setHistoryOpen(nx); if (nx) loadHistory(histQ); };
  const openBundle = async (id) => {
    try { const { data } = await api.get(`/marketing/bundle/${id}`); setBundle(data); setView("grid"); setHistoryOpen(false); }
    catch { toast.error("Could not open bundle"); }
  };
  const [reposting, setReposting] = useState(false);
  const [redrawing, setRedrawing] = useState(false);
  const duplicateBundle = async (id, opts = {}) => {
    setReposting(true);
    try {
      const { data } = await api.post(`/marketing/bundle/${id}/duplicate`, { schedule: !!opts.schedule });
      setBundle(data); setView(opts.schedule ? "calendar" : "grid"); setHistoryOpen(false);
      toast.success(opts.schedule ? "Reposted & auto-scheduled across next week" : "Reposted — a fresh copy is ready to tweak & reuse");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not repost"); }
    finally { setReposting(false); }
  };
  const redrawAllImages = async () => {
    if (!bundle?.id) return;
    setRedrawing(true);
    try {
      const { data } = await api.post(`/marketing/bundle/${bundle.id}/redraw-images`);
      setBundle(data);
      toast.info("Redrawing all graphics — your captions are kept. New images appear as they finish.");
      clearTimeout(poll.current); poll.current = setTimeout(() => pollBundle(data.id), 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not redraw graphics"); }
    finally { setRedrawing(false); }
  };

  const isProcessing = generating || bundle?.status === "processing";
  const prog = bundle?.progress || {};
  const byBrand = (b) => (bundle?.posts || []).filter((p) => p.brand === b);
  const brandName = (b) => (byBrand(b)[0]?.brand_name) || (b === "ydeal" ? "YDeal Title Services" : "Title Priority");

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-slate-100 -m-6 p-6 text-slate-800">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="font-display text-2xl font-700 tracking-tight text-[#1A395E] flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-teal" /> Marketing Hub
          </h1>
          <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">
            Your Client Acquisition Engine. One click generates a daily bundle — a tailored LinkedIn, Facebook and Instagram post for
            <b> both YDeal and Title Priority</b>, each with an AI-designed, on-brand graphic featuring your logo.
          </p>
        </div>
        <button data-testid="history-toggle-btn" onClick={toggleHistory}
          className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${historyOpen ? "border-[#1A395E] bg-[#1A395E] text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
          <History className="h-4 w-4" /> History
        </button>
      </div>

      {historyOpen && (
        <div data-testid="history-panel" className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input data-testid="history-search-input" value={histQ}
                onChange={(e) => { setHistQ(e.target.value); loadHistory(e.target.value); }}
                placeholder="Search past bundles — topic, brand, state, or text…"
                className="w-full rounded-lg border border-slate-300 pl-8 pr-3 py-2 text-sm focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal" />
            </div>
            <span className="text-xs text-slate-400">{history.length} bundle(s)</span>
            <button data-testid="history-close-btn" onClick={() => setHistoryOpen(false)} className="ml-auto rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </div>
          {history.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No bundles found.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {history.map((h) => (
                <div key={h.id} data-testid={`history-item-${h.id}`}
                  className="group relative flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2 hover:border-teal hover:shadow-sm transition-all">
                  <button onClick={() => openBundle(h.id)} className="flex flex-1 items-center gap-3 text-left min-w-0">
                    {h.thumb ? <img src={fileUrl(h.thumb)} alt="" className="h-14 w-14 rounded object-cover" /> : <div className="h-14 w-14 rounded bg-slate-100" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                        <span>{h.date}</span>
                        <span className="text-slate-300">·</span>
                        <span>{(h.brands || []).length === 2 ? "Both" : (h.brands || [])[0] === "ydeal" ? "YDeal" : (h.brands || [])[0] === "titlepriority" ? "Title Priority" : "—"}</span>
                        {h.status !== "done" && <span className="rounded bg-amber-100 px-1 text-amber-700">{h.status}</span>}
                      </div>
                      <div className="truncate text-[13px] font-semibold text-[#1A395E]">{h.topic || "Nationwide bundle"}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10px] text-slate-500">
                        <span className="rounded-full bg-slate-100 px-1.5 py-0.5">{h.post_count} posts · {h.images} imgs</span>
                        {h.tone && <span className="rounded-full bg-slate-100 px-1.5 py-0.5">{h.tone}</span>}
                        {(h.states || []).slice(0, 3).map((s) => <span key={s} className="rounded-full bg-teal/10 px-1.5 py-0.5 text-teal">{s}</span>)}
                      </div>
                    </div>
                  </button>
                  <div className="flex flex-col gap-1">
                    <button data-testid={`history-repost-${h.id}`} onClick={() => duplicateBundle(h.id)} disabled={reposting}
                      title="Duplicate & tweak this bundle"
                      className="inline-flex items-center gap-1 rounded-md border border-teal/50 px-2 py-1 text-[10px] font-semibold text-teal hover:bg-teal/5 transition-colors disabled:opacity-60">
                      {reposting ? <Loader2 className="h-3 w-3 animate-spin" /> : <CopyPlus className="h-3 w-3" />} Repost
                    </button>
                    <button data-testid={`history-repost-sched-${h.id}`} onClick={() => duplicateBundle(h.id, { schedule: true })} disabled={reposting}
                      title="Clone & auto-schedule across next week"
                      className="inline-flex items-center gap-1 rounded-md border border-[#1A395E]/50 px-2 py-1 text-[10px] font-semibold text-[#1A395E] hover:bg-[#1A395E]/5 transition-colors disabled:opacity-60">
                      <CalendarDays className="h-3 w-3" /> + Schedule
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Domain</label>
          <div className="inline-flex overflow-hidden rounded-lg border border-slate-300" data-testid="brand-select">
            {[["both", "Both"], ["ydeal", "YDeal"], ["titlepriority", "Title Priority"]].map(([val, lbl]) => (
              <button key={val} data-testid={`brand-opt-${val}`} onClick={() => setBrandSel(val)}
                className={`px-3 py-2 text-sm font-semibold transition-colors ${brandSel === val ? "bg-[#1A395E] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Topic or offer <span className="font-normal normal-case text-slate-400">(optional — steers every post)</span></label>
          <input data-testid="marketing-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)}
            placeholder='e.g. "Nationwide low-cost title search — 24h turnaround"'
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal" />
        </div>
        <div className="min-w-[140px]">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Tone</label>
          <select data-testid="marketing-tone-select" value={tone} onChange={(e) => setTone(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal">
            {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <button data-testid="generate-bundle-btn" onClick={generate} disabled={isProcessing}
          className="inline-flex items-center gap-2 rounded-lg bg-[#1A395E] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#24497A] transition-colors disabled:opacity-60">
          {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {isProcessing ? "Generating…" : "Generate Daily Bundle"}
        </button>
        <div className="w-full" data-testid="state-picker">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Target states <span className="font-normal normal-case text-slate-400">(optional — tailors copy &amp; regional hashtags; leave empty for nationwide)</span></label>
          <div className="flex flex-wrap gap-1.5">
            {STATES.map((s) => {
              const on = statesSel.includes(s);
              return (
                <button key={s} data-testid={`state-opt-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setStatesSel((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s])}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${on ? "bg-teal text-white border-teal" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                  {s}
                </button>
              );
            })}
            {statesSel.length > 0 && (
              <button data-testid="state-clear" onClick={() => setStatesSel([])}
                className="rounded-full px-2.5 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 transition-colors">Clear</button>
            )}
          </div>
        </div>
      </div>

      {isProcessing && (
        <div data-testid="bundle-progress" className="mb-5 flex items-center gap-3 rounded-lg border border-[#1A6FBF]/30 bg-blue-50 px-4 py-3 text-sm text-[#1A395E]">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Crafting posts &amp; branded graphics… {prog.done || 0} / {prog.total || 6} ready. This can take a minute — new cards appear as they finish.</span>
        </div>
      )}

      {!bundle && !isProcessing && (
        <div data-testid="marketing-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Megaphone className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No bundles yet. Add a topic (optional), pick a tone, then click <b>Generate Daily Bundle</b>.</p>
        </div>
      )}

      {bundle && (bundle.posts || []).length > 0 && (
        <div className="space-y-6" data-testid="bundle-results">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            {bundle.date && <span>Bundle · {bundle.date} · {bundle.status === "done" ? `${bundle.images} graphics` : "in progress"}</span>}
            {bundle.tone && <span className="rounded-full bg-slate-200 px-2 py-0.5 normal-case text-slate-600">Tone: {bundle.tone}</span>}
            {bundle.topic && <span className="rounded-full bg-teal/15 px-2 py-0.5 normal-case text-teal">Topic: {bundle.topic}</span>}
            {(bundle.states || []).length > 0 && <span data-testid="bundle-states-chip" className="rounded-full bg-[#1A395E]/10 px-2 py-0.5 normal-case text-[#1A395E]">States: {bundle.states.join(", ")}</span>}
            {bundle.duplicated_from && <span data-testid="bundle-reposted-chip" className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 normal-case text-amber-700"><CopyPlus className="h-3 w-3" /> Reposted{bundle.duplicated_from_date ? ` from ${bundle.duplicated_from_date}` : ""}</span>}
            <div className="ml-auto flex items-center gap-2">
              <div className="inline-flex overflow-hidden rounded-lg border border-slate-300">
                <button data-testid="view-grid-btn" onClick={() => setView("grid")}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold normal-case transition-colors ${view === "grid" ? "bg-[#1A395E] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  <LayoutGrid className="h-3.5 w-3.5" /> Grid
                </button>
                <button data-testid="view-calendar-btn" onClick={() => setView("calendar")}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold normal-case transition-colors ${view === "calendar" ? "bg-[#1A395E] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  <CalendarDays className="h-3.5 w-3.5" /> Calendar
                </button>
              </div>
              <button data-testid="schedule-week-btn" onClick={scheduleWeek} disabled={scheduling}
                className="inline-flex items-center gap-1.5 rounded-lg border border-teal/60 bg-white px-3 py-1 text-[11px] font-semibold normal-case text-teal hover:bg-teal/5 transition-colors disabled:opacity-60">
                {scheduling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarDays className="h-3.5 w-3.5" />} Schedule across the week
              </button>
              <button data-testid="redraw-all-btn" onClick={redrawAllImages} disabled={redrawing || isProcessing}
                className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-400/60 bg-white px-3 py-1 text-[11px] font-semibold normal-case text-fuchsia-700 hover:bg-fuchsia-50 transition-colors disabled:opacity-60">
                {redrawing || isProcessing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Redraw all images
              </button>
              <button data-testid="repost-bundle-btn" onClick={() => duplicateBundle(bundle.id)} disabled={reposting || isProcessing}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#1A395E]/50 bg-white px-3 py-1 text-[11px] font-semibold normal-case text-[#1A395E] hover:bg-[#1A395E]/5 transition-colors disabled:opacity-60">
                {reposting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CopyPlus className="h-3.5 w-3.5" />} Repost
              </button>
              <button data-testid="repost-schedule-btn" onClick={() => duplicateBundle(bundle.id, { schedule: true })} disabled={reposting || isProcessing}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#1A395E] px-3 py-1 text-[11px] font-semibold normal-case text-white hover:bg-[#24497A] transition-colors disabled:opacity-60">
                {reposting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarDays className="h-3.5 w-3.5" />} Repost + schedule
              </button>
            </div>
          </div>

          {view === "calendar" ? (
            <CalendarView bundle={bundle} onSchedulePost={schedulePost} />
          ) : (
            <div className="space-y-8">
              {BRAND_ORDER.filter((b) => byBrand(b).length > 0).map((b) => (
                <div key={b} data-testid={`brand-section-${b}`}>
                  <h2 className="mb-3 flex items-center gap-2 text-lg font-600 text-[#1A395E]">
                    <span className="inline-block h-4 w-1.5 rounded-full bg-teal" /> {brandName(b)}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {byBrand(b).map((p) => <PostCard key={p.id} post={p} bundleId={bundle.id} onRegenerated={onRegenerated} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
